/**
 * Split a message's attachments into the three things the bubble renders them as.
 *
 * The wire carries one `attachments` array; audio renders as a player, media as a
 * thumbnail grid, and everything else as a list of document rows. Splitting here rather
 * than inside the renderers keeps each of them unaware of the other two kinds, and gives
 * the bubble, the classic row and the pinned strip one shared derivation instead of three
 * drifting copies.
 */

import type { AudioAttachment } from "@/features/shared/audio-recorder";
import type { AttachmentWithStatus } from "@/features/shared/upload";
import {
  isImage,
  isVideo,
  resolveAttachmentFileExtension,
} from "@/libs/file-helper";
import type { ChannelMessage } from "../_api/channel-endpoints";

type ChannelAttachment = NonNullable<ChannelMessage["attachments"]>[number];

/**
 * Is this row something the media grid can paint?
 *
 * Two signals answer that and they disagree often enough to matter: the wire `type` is
 * the server's word, while the extension is what the renderers downstream actually read
 * (the `Attachment` tile decides picture-vs-icon from it). Either one claiming image or
 * video is enough — a document that lands in the grid costs only the icon tile it had
 * before this split existed, while a photo pushed into the document list would lose its
 * preview outright.
 */
function isMediaAttachment(attachment: NonNullable<ChannelAttachment>) {
  if (attachment.type === "PHOTO" || attachment.type === "VIDEO") {
    return true;
  }

  const fileExtension = resolveAttachmentFileExtension({
    fileName: attachment.fileName,
    contentUri: attachment.contentUri,
  });

  return isImage(fileExtension) || isVideo(fileExtension);
}

export function splitAttachments(attachments: ChannelMessage["attachments"]) {
  const list = (attachments ?? []).filter(
    (a): a is NonNullable<ChannelAttachment> => a != null,
  );
  const rest = list.filter((a) => a.type !== "AUDIO");

  return {
    audioAttachments: list
      .filter((a) => a.type === "AUDIO")
      .sort(
        (a, b) =>
          new Date(a.createdAt ?? 0).getTime() -
          new Date(b.createdAt ?? 0).getTime(),
      ),
    mediaAttachments: rest.filter(isMediaAttachment),
    documentAttachments: rest.filter((a) => !isMediaAttachment(a)),
  };
}

/**
 * The same message, in the two lists the composer edits it with.
 *
 * Channel attachments are already-uploaded rows while the composer's types are the
 * uploader's in-flight shapes, so everything enters as `status: "done"` — the casts are
 * that seam (`AttachmentWithStatus` is the deprecated shared type the composer declares).
 *
 * Voice notes go to `audios`, not `attachments`, so the editor draws them as players the
 * way the bubble does; left among the files they seed as a generic tile, and a recording
 * made while editing would sit beside its own kind in a different form. The split never
 * reaches the wire — `uploads` is the union of the two, so it round-trips — and each list
 * keeps the order it had on the message.
 *
 * It lives here rather than beside the editor because the AUDIO discriminator is this
 * module's to know; `splitAttachments` above is the same decision for rendering.
 */
export function toComposerUploads(message: ChannelMessage) {
  const list = (message.attachments ?? []).filter(
    (a): a is NonNullable<ChannelAttachment> => a != null,
  );

  return {
    attachments: list
      .filter((attachment) => attachment.type !== "AUDIO")
      .map(
        (attachment) =>
          ({
            ...attachment,
            status: "done",
            originalContentUri: attachment.contentUri,
          }) as unknown as AttachmentWithStatus,
      ),
    audios: list
      .filter((attachment) => attachment.type === "AUDIO")
      .map(
        (attachment) =>
          ({
            id: attachment.id,
            status: "done",
            percent: 100,
            originalContentUri: attachment.contentUri ?? undefined,
          }) satisfies AudioAttachment,
      ),
  };
}
